import React from 'react';
import { Axis, AxisType } from './axis';
import { Legend } from './legend';
import { Series } from './series';

type LineGraphCanvasProps = {
    width?    : number;
    height?   : number;
    minWidth? : number;
    series    : Series[];
};

enum DeviceType {
    mobile = '18.75rem',  //300px default
    tablet = '15rem',
    laptop = '15rem',
    desktop = '18.75rem'
}

export class TrainingGraph extends React.Component<LineGraphCanvasProps> {
    private canvasRef     : React.RefObject<HTMLCanvasElement | null>;
    private xAxis         : Axis;
    private isLeftActive  : boolean;
    private isRightActive : boolean;
    private leftAxis      : Axis;
    private rightAxis     : Axis;
    private legend        : Legend;
    private onlyLegend    : boolean;

    static defaultProps = {
        width: 600,
        height: 300,
        series: [],
    };

    constructor(props : LineGraphCanvasProps) {
        super(props);

        this.canvasRef      = React.createRef<HTMLCanvasElement>();
        this.isLeftActive   = false;
        this.isRightActive  = false;
        this.legend         = new Legend();
        this.xAxis          = new Axis(AxisType.Bottom, 25);
        this.xAxis.stepSize = 1.0;
        this.leftAxis       = new Axis(AxisType.Left,   20);
        this.leftAxis.unit  = "%";
        this.leftAxis.desc  = "Accuracy";
        this.leftAxis.stepSize = 10;
        this.rightAxis      = new Axis(AxisType.Right,  20);
        this.rightAxis.desc = "Training Loss (PPL)";
        this.xAxis.desc     = "Steps";
        this.onlyLegend     = false;
    }

    resizeListener = () => {
        this.updateCanvasSize();
    };

    componentDidMount() 
    {
        this.updateCanvasSize();
        window.addEventListener("resize", this.resizeListener);
        this.scaleAxes();
        this.drawGraph();
    }

    componentDidUpdate() {
        this.scaleAxes();
        this.drawGraph();
    }

    componentWillUnmount(): void {
        window.removeEventListener("resize", this.resizeListener);
    }

    updateCanvasSize() {
        let type = this.getDeviceType();
        let canvasEl = this.canvasRef.current;
        if (canvasEl) {
            let height: string = type;
            canvasEl.style.height = height;
        }
    }

    drawGraph()
    {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { width, height } = this.props;
 
        // Clear canvas
        ctx.clearRect(0, 0, width ?? 600, height ?? 300);

        if (this.onlyLegend) {
            this.legend.draw(ctx, this.props.series);
            return;
        }

        this.xAxis.draw(ctx);
        if (this.isLeftActive) {
            this.leftAxis.draw(ctx);
        }
        if (this.isRightActive) {
            this.rightAxis.draw(ctx);
        }
        this.legend.draw(ctx, this.props.series);

        this.drawGrid(ctx);
 
        for(const oneSeries of this.props.series) {
            oneSeries.drawLines(ctx, this.xAxis, this.getAxis(oneSeries.axisType));
        }

        if (this.shouldDrawDots()) {
            for(const oneSeries of this.props.series) {
                oneSeries.drawDots(ctx, this.xAxis, this.getAxis(oneSeries.axisType));
            }
        }
    }

    private drawGrid(ctx : CanvasRenderingContext2D)
    {
        let vertTicks;
        let top;
        let bottom;
        if (this.isLeftActive) {
            vertTicks = this.leftAxis.ticks;
            top       = this.leftAxis.top;
            bottom    = this.leftAxis.bottom;
        }
        else {
            vertTicks = this.rightAxis.ticks;
            top       = this.rightAxis.top;
            bottom    = this.rightAxis.bottom;
        }

        let horizontalTicks = this.xAxis.ticks;
        ctx.strokeStyle = '#00000040';
        ctx.lineWidth   = 1;
        ctx.setLineDash([2,2]);

        for (const y of vertTicks) {
            ctx.beginPath()
            ctx.moveTo(this.xAxis.left, y);
            ctx.lineTo(this.xAxis.right, y);
            ctx.stroke();
        }

        for (const x of horizontalTicks) {
            ctx.beginPath()
            ctx.moveTo(x, top);
            ctx.lineTo(x, bottom);
            ctx.stroke();
        }
    }

    getAxis(axisType : AxisType) : Axis
    {
        switch(axisType) {
            case AxisType.Left :
                return this.leftAxis;
            case AxisType.Right :
                return this.rightAxis;
            case AxisType.Bottom :
                return this.xAxis;
        }
    }

    getDeviceType(): DeviceType {
        const width = window.innerWidth;
        
        if (width < 768) return DeviceType.mobile;
        if (width < 1024) return DeviceType.tablet;
        if (width < 2560) return DeviceType.laptop;
        
        return DeviceType.desktop;
    }

    render() {
        const { width, height } = this.props;

        return (
          <canvas
            ref={this.canvasRef}
            width={width}
            height={height}
            style={{ border: '1px solid #000' }}
          />
        );
    }

    scaleAxes()
    {
        const width       = this.props.width ?? 600;
        const height      = this.props.height ?? 300;
        const btnPadding  = 44;
        const leftPadding = 60;
        const legendWidth = 180;
        this.onlyLegend   = false;
        if (this.props.minWidth !== undefined && this.props.minWidth > width) {
            this.onlyLegend = true;
            this.legend.setWindowRect(0, 0, width, height);
            return;
        }

        this.isLeftActive  = false;
        this.isRightActive = false;
        let maxX     = 0;
        let maxLeft  = 0;
        let maxRight = 0;
        for(const oneSeries of this.props.series) {
            maxX = Math.max(maxX, oneSeries.getMaxX());
            if (oneSeries.axisType === AxisType.Left) {
                maxLeft            = Math.max(maxLeft, oneSeries.getMaxY());
                this.isLeftActive  = true;
            }
            else {
                maxRight           = Math.max(maxRight, oneSeries.getMaxY());
                this.isRightActive = true;
            }
        }

        let left                     = this.leftAxis;
        let right : Axis | undefined = this.rightAxis;

        if (!this.isLeftActive) {
            left  = this.rightAxis;
            right = undefined;
        }
        else if (!this.isRightActive) {
            right = undefined;
        }

        if (left !== undefined && right !== undefined) {
            // Both left and right axis
            this.xAxis.setWindowRect(leftPadding, height - btnPadding, width - leftPadding*2 - legendWidth, btnPadding);
        }
        else {
            // Only left
            this.xAxis.setWindowRect(leftPadding, height - btnPadding, width - leftPadding - legendWidth, btnPadding);
        }

        left.setWindowRect(0, 0, leftPadding, height - btnPadding);
        left.type = AxisType.Left;
        if (right !== undefined) {
            right.type = AxisType.Right;
            right.setWindowRect(width - leftPadding - legendWidth, 0, leftPadding, height - btnPadding);
        }
        this.legend.setWindowRect(width - legendWidth, 0, legendWidth, height);

        this.xAxis.setRange(0, maxX);
        this.leftAxis.setRange(0, maxLeft);
        this.rightAxis.setRange(0, maxRight);
    }

    shouldDrawDots() : boolean
    {
        let maxDots = 0;

        for(const oneSeries of this.props.series) {
            maxDots = Math.max(maxDots, oneSeries.getMaxX());
        }

        if (maxDots === 0) {
            return true;
        }

        if (this.xAxis.width / maxDots < 25) {
            return false;
        }

        return true;
    }
}

